import {ErrorReportingService} from "../services/errorReportingService";
import {AppStateService} from "../services/appStateService";
import {PriceFormatter} from "../services/priceFormatter";
import {DrawingOptionsService} from "./drawingComponent/drawingOptionsService";

export class UIService {

    private templateAttributeName = 'nf-configurator-option-template';
    private containerAttributeName = 'nf-configurator-options-container';
    public resetButtonAttributeName = 'nf-reset-btn-module';
    private errorReportingService: ErrorReportingService;
    private appStateService: AppStateService;
    private priceFormatter: PriceFormatter;

    constructor() {
        this.errorReportingService = new ErrorReportingService();
        this.appStateService = new AppStateService();
        this.priceFormatter = new PriceFormatter();
    }

    initBasicBehaviours(initialModule: string = null)
    {
        this.initTopNoticeBehaviour();
        this.initializeConfiguratorMenuBehaviour();
        this.initializeConfiguratorTaggleButtonBehaviour();
        this.initializeComponentHeaderOkButton();
        this.initializeStepsButtons();
        // this.activateInitialStep();
    }

    initTopNoticeBehaviour()
    {
        const actionElements = this.getElementByActionName('closeTopNotice');

        actionElements.forEach((element) => {
            element.addEventListener('click', function(event) {
                const templateFlag = this.getAttributeFromElement(event.currentTarget, 'nf-template-flag');
                this.appStateService.setAppSettingTemplateFlag('app', templateFlag, false);
                this.refreshTemplate('app');
            }.bind(this));
        });

    }

    getMetaTagContent(tagName: string)
    {
        return document
            .querySelector(`meta[name="${tagName}"]`)
            .getAttribute('content')
    }

    getAttributeFromElementById(elementId: string, attributeName:string)
    {
        const element = document.getElementById(elementId);
        return element.getAttribute(attributeName);
    }

    getAttributeFromElement(element, attributeName:string)
    {
        return element.getAttribute(attributeName);
    }

    getElementById(elementId: string)
    {
        return document.getElementById(elementId);
    }

    getElementByClassName(elementClass: string)
    {
        return document.querySelector(`.${elementClass}`);
    }

    getElementByActionName(actionName: string)
    {
        return Array.from(
            document.querySelectorAll(`[nf-action="${actionName}"]`)
        );
    }

    getElementsByPartName(attributeName: string)
    {
        return document.querySelectorAll(`[nf-option-name=${attributeName}]:not([nf-configurator-option-template])`);
    }

    getElementByPartNameAndId(attributeName: string, id: any)
    {
        return document.querySelector(`[nf-option-name="${attributeName}"][nf-option-id="${id}"]`);
    }

    getElementBySelector(selector: string)
    {
        return document.querySelector(selector);
    }

    getAllElementsByValueKeyAttribute(attributeValue: string)
    {
        return Array.from(
            document.querySelectorAll(`[nf-value-key="${attributeValue}"]`)
        );
    }

    getElementByValueKeyAttribute(element: HTMLElement, attributeValue: string)
    {
        return element.querySelector(`[nf-value-key="${attributeValue}"]`);
    }

    getElementByAttribute(attributeName: string, attributeValue: string, element: HTMLElement | Element | Document = document)
    {
        let elem = element.querySelector(`[${attributeName}="${attributeValue}"]`);
        elem = this.validateHtmlElement(elem, `[${attributeName}="${attributeValue}"]`);
        return elem;
    }

    getElementsByAttribute(attributeName: string, attributeValue: string, element: HTMLElement | Document = document) {
        return this.validateHtmlElement(
            Array.from(element.querySelectorAll(`[${attributeName}="${attributeValue}"]`)),
            `[${attributeName}="${attributeValue}"]`
        );
    }

    getElementsByClassName(classname: string)
    {
        return document.querySelectorAll(`.${classname}`);
    }

    setAttributeToElement(element, attributeName: string, value: string)
    {
        element.setAttribute(attributeName, value);
    }

    addTextInElement(element, text)
    {
        if (element === null) {
            return false;
        }
        if (text !== null && text !== '') {
            element.appendChild(
                document.createTextNode(text)
            );
        }
    }

    getTemplateInput(templateInputAttributeValue: string): HTMLInputElement
    {
        return this.validateHtmlElement(
            document.querySelector(`[nf-template-input="${templateInputAttributeValue}"]`),
            `[nf-template-input="${templateInputAttributeValue}"]`
        );
    }

    addHtmlInElement(element, html)
    {
        if (html !== null && html !== '') {
            element.innerHTML = html;
        }
    }

    removeHtmlInElement(element)
    {
        element.innerHTML = '';
    }

    setBackgroundColorToElement(element, color)
    {
        element.style.backgroundColor = color;
    }

    replaceTextInElement(element, text)
    {
        if (text !== null && text !== '') {
            element.textContent = text;
        }
    }

    createElement(container, element: string, text = null)
    {
        let createdElement = document.createElement(element);

        if (text !== null) {
            this.addTextInElement(createdElement, text);
        }
        container.appendChild(createdElement);
    }

    /*
     * Option parameter can be just simple array of values eg. [1, 3, 5]
     * or it can be an array of objects like {name: exampleName, value: example value}
     * With this solution we can create select input with same name and value or different ones
     */
    createInputElement(container, element: string, option = null, value = null)
    {
        // @ts-ignore
        let createdElement: HTMLInputElement = document.createElement(element);

        if (option.name !== null) {
            const optionName = typeof option.name !== 'undefined' ? option.name : option;
            this.addTextInElement(createdElement, optionName);
            createdElement.value = optionName;
        }

        if (option.value !== null) {
            const optionValue = typeof option.value !== 'undefined' ? option.value : option;
            this.setAttributeToElement(container, 'value', optionValue);
            createdElement.value = optionValue;
        }

        container.appendChild(createdElement);
    }

    disableOption(element: Element)
    {
        element.classList.add('--blocked');
    }

    enableOption(element: Element)
    {
        element.classList.remove('--blocked');
    }

    isOptionDisabled(element: Element)
    {
        return element.classList.contains('--blocked');
    }

    hideElement(element: Element)
    {
        element.classList.add('--hiddenTemplateElement');
    }

    isHiddenElement(element: Element): boolean
    {
        return element.classList.contains('--hiddenTemplateElement');
    }

    hideConfiguratorMenuElement(componentName: string)
    {
        const element = document.querySelector(`.configuratorMenu__category-item[nf-component-name="${componentName}"]`);
        if (element) {
            element.classList.add('--hiddenTemplateElement');
        }
    }

    showElement(element: Element)
    {
        element.classList.remove('--hiddenTemplateElement');
    }

    refreshElementsInModule(module :string)
    {
        Object.entries(this.appStateService.getAppSettingTemplateFlagsByModule(module)).forEach(([element, visibility]) => {
            const flagElements = Array.from(
                document.querySelectorAll(
                    `[nf-template-flag="${element}"]`
                )
            );
            flagElements.forEach((flagElement) => {
                if (!visibility) {
                    this.hideElement(flagElement);
                } else {
                    this.showElement(flagElement);
                }
            });
        });
    }

    createConfiguratorOption(attributeValue: string)
    {
        const option = this.getElementByAttribute(this.templateAttributeName, attributeValue).cloneNode(true) as HTMLElement;
        if (!option) {
            throw new Error("Template or container attribute is missing");
        }

        this.getElementByAttribute(this.containerAttributeName, attributeValue).appendChild(option);
        option.removeAttribute(this.templateAttributeName);
        return option;
    }

    moveElement(elementId, moveBeforeElementId)
    {
        const elem = document.querySelector(`#${elementId}`);
        const destination = document.querySelector(`#${moveBeforeElementId}`);
        destination.parentNode.insertBefore(elem, destination);
    }

    resetSelection(options) {
        options.forEach((option) => {
            option.classList.remove('--checked-item');
        });
    }

    resetSelectionsByPartName(partName: string)
    {
        this.resetSelection(
            Array.from(this.getElementsByPartName(partName))
        );
    }

    public isInEditMode()
    {
        const editSource = this.getAttributeFromElementById('app', 'editsource');

        if (editSource === '' || editSource === null) {
            return false;
        }
        return true;
    }

    public removeElementsByPartName(partName: string)
    {
        const elements = this.getElementsByPartName(partName);
        elements.forEach((element) => {
            element.remove();
        });
    }

    public setSelectedOption(option) {
        if (option === null || option === undefined) {
            return;
        }
        option.classList.add('--checked-item');
    }

    public setSelectedOptions(partName: string, options) {
        options.forEach((option) => {
            const element = this.getElementByPartNameAndId(
                partName,
                option.id,
            );
            element.classList.add('--checked-item');
        });
    }

    public updatePriceModule(pricesObject) {
        const currentGrossPriceElement = Array.from(this.getElementsByAttribute('nf-value', 'currentGrossPriceValue'));
        const savedGrossMoneyElement = Array.from(this.getElementsByAttribute('nf-value', 'savedGrossMoney'));
        const ordinaryPriceElement = Array.from(this.getElementsByAttribute('nf-value', 'ordinaryPrice'));
        const currentNetPriceElement = Array.from(this.getElementsByAttribute('nf-value', 'currentNetPriceValue'));
        const savedNetMoneyElement = Array.from(this.getElementsByAttribute('nf-value', 'savedNetMoney'));
        const savedRegularCustomerGrossMoneyElement = Array.from(this.getElementsByAttribute('nf-value', 'savedRegularCustomerGrossMoney'));
        const currentRegularCustomerGrossPriceElement = Array.from(this.getElementsByAttribute('nf-value', 'currentRegularCustomerGrossPriceValue'));

        if (pricesObject.price === 0) {
            this.appStateService.setAppSettingTemplateFlag('app', 'regularCustomerPrice', false);
            this.appStateService.setAppSettingTemplateFlag('app', 'b2bCustomerPrice', false);
            this.appStateService.setAppSettingTemplateFlag('app', 'noPriceInfo', true);
            this.disableOption(this.getElementByAttribute('nf-template-action', 'addToCartButton'));
            this.refreshElementsInModule('app');

            return;
        } else {
            this.appStateService.setAppSettingTemplateFlag('app', 'regularCustomerPrice', true);
            this.appStateService.setAppSettingTemplateFlag('app', 'b2bCustomerPrice', true);
            this.appStateService.setAppSettingTemplateFlag('app', 'noPriceInfo', false);
            this.enableOption(this.getElementByAttribute('nf-template-action', 'addToCartButton'));
            this.refreshElementsInModule('app');
        }

        const currentGrossPrice =  this.priceFormatter.getPriceWithCurrency(Math.ceil(pricesObject.price - pricesObject.discount));
        const currentNetPrice = this.priceFormatter.getPriceWithCurrency(Math.ceil(pricesObject.netPrice - pricesObject.netDiscount));
        const savedGrossMoney = this.priceFormatter.getPriceWithCurrency(pricesObject.discount);
        const savedNetMoney = this.priceFormatter.getPriceWithCurrency(pricesObject.netDiscount);
        const ordinaryPrice = this.priceFormatter.getPriceWithCurrency(pricesObject.price);
        const currentRegularCustomerGrossPrice = this.priceFormatter.getPriceWithCurrency(Math.ceil(pricesObject.price - pricesObject.discountValueForRegularCustomer));
        const savedRegularCustomerGrossMoney = this.priceFormatter.getPriceWithCurrency(pricesObject.discountValueForRegularCustomer);

        currentGrossPriceElement.forEach((element) => {
            this.replaceTextInElement(element, currentGrossPrice);
        });
        currentNetPriceElement.forEach((element) => {
            this.replaceTextInElement(element, currentNetPrice);
        });
        savedGrossMoneyElement.forEach((element) => {
            this.replaceTextInElement(element, savedGrossMoney);
        });
        savedNetMoneyElement.forEach((element) => {
            this.replaceTextInElement(element, savedNetMoney);
        });
        ordinaryPriceElement.forEach((element) => {
            this.replaceTextInElement(element, ordinaryPrice);
        });
        currentRegularCustomerGrossPriceElement.forEach((element) => {
            this.replaceTextInElement(element, currentRegularCustomerGrossPrice);
        });
        savedRegularCustomerGrossMoneyElement.forEach((element) => {
            this.replaceTextInElement(element, savedRegularCustomerGrossMoney);
        });
    }

    public initializeConfiguratorMenuBehaviour()
    {
        const activeComponents = this.appStateService.getAppSettingOption('activeComponents');
        const menuItems = Array.from(this.getElementsByClassName('configuratorMenu__category-item'));

        menuItems.forEach((menuItem) => {
            if (activeComponents.includes(menuItem.getAttribute('nf-component-name'))) {
                menuItem.classList.remove('--hidden');

                menuItem.addEventListener('click', (event: any) => {
                    const componentName = this.getAttributeFromElement(event.currentTarget, 'nf-component-name');

                    if (this.isStepOpened(componentName)) {
                        this.closeStep(componentName);
                    } else {
                        this.openStep(componentName);
                    }
                })
            }
        })
    }

    public initializeConfiguratorTaggleButtonBehaviour()
    {
        const menuToggleButtons = Array.from(this.getElementsByClassName('configuratorPanel__toggle-btn'));

        menuToggleButtons.forEach((menuToggleButton) => {
            menuToggleButton.addEventListener('click', (event: any) => {
                let componentName = this.getAttributeFromElement(
                    event.currentTarget.parentElement,
                    'nf-component-name'
                );

                // if (componentName === null) {
                //     componentName = this.getAttributeFromElement(
                //         event.currentTarget.parentElement.querySelectorAll(),
                //         'nf-component-name'
                //     );
                // }

                // console.log([
                //     event.currentTarget,
                //     componentName,
                // ]);

                if (this.isStepOpened(componentName)) {
                    this.closeStep(componentName);
                } else {
                    this.openStep(componentName);
                }
            })
        })
    }

    public updateDrawingComponent(drawingConfig)
    {
        const iccDrawApp = document.getElementsByTagName('app-drawing')[0];
        // @ts-ignore
        iccDrawApp.drawingConfig = drawingConfig;
    }

    public updateDrawingSide(value)
    {
        const options = new DrawingOptionsService().getDefaultDrawingOptions();
        options.base.side = value;
        const iccDrawApp = document.getElementsByTagName('app-drawing')[0];
        // @ts-ignore
        iccDrawApp.options = options;
    }

    public updateDrawingOptions(options = null)
    {
        if (options === null) {
            options = new DrawingOptionsService().getDefaultDrawingOptions();
        }

        const iccDrawApp = document.getElementsByTagName('app-drawing')[0];
        // @ts-ignore
        iccDrawApp.options = options;
    }

    public refreshTemplate(moduleName: string)
    {
        const templateFlags = this.appStateService.getTemplateFlags(moduleName);
        let templateFlagAttributeName = 'nf-template-flag';

        for (const [templateFlagValue, visibility] of Object.entries(templateFlags)) {
            const elements = Array.from(
                document.querySelectorAll(
                    `[${templateFlagAttributeName}="${templateFlagValue}"]`
                )
            );
            if (elements.length === 0) {
                this.errorReportingService.display(`There are no elements with specified template flag (flag name: ${templateFlagValue})`);
                continue;
            }
            elements.forEach((element) => {
                if (!visibility) {
                    this.hideElement(element);
                } else {
                    this.showElement(element);
                }
            });
        }
    }

    /*
     * Sometimes when we try to get some element from the template we get a "null" because element does not exist.
     * Then we can use this fake element to attach event listener for example.
     * Giving fake element prevents getting error during script execution.
     */
    public validateHtmlElement(elements, elementAttributes: string = '')
    {
        if (elements === null) {
            this.errorReportingService.display(`There is no element with specified attribute/class/id/tag (details: ${elementAttributes})`);
            return document.createElement("span");
        }

        if (Array.isArray(elements) && elements.length === 0) {
            this.errorReportingService.display(`There is no element with specified attribute/class/id/tag (details: ${elementAttributes})`);
            elements = [document.createElement("span")];
        }

        return elements;
    }

    public getResetButtonByModule(module): Element
    {
        return document.querySelector(`[${this.resetButtonAttributeName}="${module}"]`);
    }

    public replaceSelectOptions(containerElement: any, options, selectedValue = null)
    {
        let lastChild;
        while (lastChild = containerElement.lastChild) {
            containerElement.removeChild(lastChild);
        }
        options.forEach((option) => {
            this.createInputElement(containerElement, 'option', option);
        });

        if (selectedValue) {
            containerElement.value = selectedValue
        }
    }

    public checkOptionInSelect(selector, optionValue) {
        selector.querySelectorAll('option').forEach((option) => {
            if (option.value == optionValue) {
                option.selected = true;
            }
        });
    }

    displayTopNotification(templateFlag: string)
    {
        this.appStateService.setAppSettingTemplateFlag('app', templateFlag, true);
        this.refreshElementsInModule('app');

        setTimeout(
            function() {
                this.appStateService.setAppSettingTemplateFlag('app', templateFlag, false);
                this.refreshElementsInModule('app');
            }
            .bind(this),
            7500
        );
    }

    private initializeComponentHeaderOkButton()
    {
        const okButtons = Array.from(this.getElementsByClassName('configuratorComponent__header-ok-btn'));
        okButtons.forEach((okButton) => {
            okButton.addEventListener('click', () => {
                document.querySelector(`#configurator__componentWrapper`).classList.remove('--active');
            });
        });
    }

    private initializeStepsButtons()
    {
        //here we want ho hide prev/next button the first and last step
        const activeComponents = this.appStateService.getAppSettingOption('activeComponents');
        const firstComponent = activeComponents[0];
        const lastComponent = activeComponents[activeComponents.length - 1];
        const firstComponentPrevButton: HTMLElement = document.querySelector(`.configuratorComponent[nf-component-name="${firstComponent}"] .configuratorComponent__header-prev-btn`);

        if (firstComponentPrevButton !== null) {
            firstComponentPrevButton.style.visibility = 'hidden';
            firstComponentPrevButton.style['pointer-events'] = 'none';
        }
        const lastComponentNextButton: HTMLElement = document.querySelector(`.configuratorComponent[nf-component-name="${lastComponent}"] .configuratorComponent__header-next-btn`);

        if (lastComponentNextButton !== null) {
            lastComponentNextButton.style.visibility = 'hidden';
            lastComponentNextButton.style['pointer-events'] = 'none';
        }
        const nextButtons = Array.from(this.getElementsByClassName('configuratorComponent__header-next-btn'));

        nextButtons.forEach((nextButton) => {
            nextButton.addEventListener('click', () => {
                const currentActiveComponent = document.querySelector('.configuratorComponent.--active');
                const componentName = this.getAttributeFromElement(currentActiveComponent, 'nf-component-name');
                const activeComponents = this.appStateService.getAppSettingOption('activeComponents');

                const index = activeComponents.indexOf(componentName);
                const nextComponentName = activeComponents[index+1];
                const configuratorComponents = Array.from(this.getElementsByClassName('configuratorComponent'));
                configuratorComponents.forEach((item) => {
                    item.classList.add('--hidden');
                    item.classList.remove('--active');
                })
                const newActiveComponent = document.querySelector(`.configuratorComponent[nf-component-name="${nextComponentName}"]`);
                newActiveComponent.classList.remove('--hidden');
                newActiveComponent.classList.add('--active');
            });
        });

        const prevButtons = Array.from(this.getElementsByClassName('configuratorComponent__header-prev-btn'));

        prevButtons.forEach((prevButton) => {
            prevButton.addEventListener('click', () => {
                const currentActiveComponent = document.querySelector('.configuratorComponent.--active');
                const componentName = this.getAttributeFromElement(currentActiveComponent, 'nf-component-name');
                const activeComponents = this.appStateService.getAppSettingOption('activeComponents');

                const index = activeComponents.indexOf(componentName);
                const nextComponentName = activeComponents[index-1];
                const configuratorComponents = Array.from(this.getElementsByClassName('configuratorComponent'));
                configuratorComponents.forEach((item) => {
                    item.classList.add('--hidden');
                    item.classList.remove('--active');
                })
                const newActiveComponent = document.querySelector(`.configuratorComponent[nf-component-name="${nextComponentName}"]`);
                newActiveComponent.classList.remove('--hidden');
                newActiveComponent.classList.add('--active');
            });
        });
    }

    public activateInitialStep()
    {
        const activeComponents = this.appStateService.getAppSettingOption('activeComponents');
        const firstComponent = activeComponents[0];

        this.activateStep(firstComponent);
    }

    public activateStep(componentName)
    {
        // const activeComponents = this.appStateService.getAppSettingOption('activeComponents');
        const menuItems = Array.from(this.getElementsByClassName('configuratorMenu__category-item'));
        const configuratorComponents = Array.from(this.getElementsByClassName('configuratorComponent'));

        configuratorComponents.forEach((item) => {
            item.classList.add('--hidden');
            item.parentElement.classList.add('--closed');
            item.parentElement.classList.remove('--opened');
        });
        const newActiveComponent = document.querySelector(`.configuratorComponent[nf-component-name="${componentName}"]`);

        newActiveComponent.classList.remove('--hidden');
        newActiveComponent.parentElement.classList.remove('--closed');
        newActiveComponent.parentElement.classList.add('--opened');
    }

    public openStep(componentName)
    {
        const component = document.querySelector(`.configuratorComponent[nf-component-name="${componentName}"]`);

        component.classList.remove('--hidden');
        component.classList.add('--active');
        // component.parentElement.classList.remove('--closed');
        component.parentElement.classList.add('--opened');
    }

    public closeStep(componentName)
    {
        const component = document.querySelector(`.configuratorComponent[nf-component-name="${componentName}"]`);

        component.classList.add('--hidden');
        component.classList.remove('--active');
        // component.parentElement.classList.add('--closed');
        component.parentElement.classList.remove('--opened');
    }

    public isStepOpened(componentName)
    {
        const component = document.querySelector(`.configuratorComponent[nf-component-name="${componentName}"]`);

        return component.classList.contains('--active');
    }
}
